import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Loader2, Rocket } from "lucide-react";

/**
 * AdminSbtContact — single-doc settings editor for the marketing
 * Contact page + Footer contact info + social links.
 */
export default function AdminSbtContact() {
  const [form, setForm] = useState({
    recipient_email: "",
    support_email: "",
    support_phone: "",
    support_hours: "",
    map_lat: "",
    map_lng: "",
    offices: [],
    socials: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // "Publish Now" — bypasses the 60s auto-publish debounce that admins
  // hit when they save and then look at /contact/ within a minute. In
  // production the debounced task can also get lost when the FastAPI
  // worker that scheduled it gets recycled before the 60s wait fires,
  // leading to "saved but not reflecting" reports. Manual button gives
  // deterministic behaviour.
  const [publishing, setPublishing] = useState(false);

  const publishNow = async () => {
    if (!window.confirm(
      "Kick off an immediate Astro rebuild? Takes ~15s. Any unsaved form changes above will NOT be included — save first."
    )) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/sbt-site/publish");
      toast.success(
        `Publishing… site rebuild started ${data.started_at ? `at ${new Date(data.started_at).toLocaleTimeString()}` : ""}. Refresh /contact/ in ~15 seconds.`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sbt-site/contact-settings");
      setForm({
        recipient_email: r.data.recipient_email || "",
        support_email:   r.data.support_email   || "",
        support_phone:   r.data.support_phone   || "",
        support_hours:   r.data.support_hours   || "",
        map_lat:         r.data.map_lat ?? "",
        map_lng:         r.data.map_lng ?? "",
        offices:         r.data.offices || [],
        socials:         r.data.socials || [],
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load contact settings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (list, idx, k, v) => setForm((f) => ({
    ...f,
    [list]: f[list].map((it, i) => i === idx ? { ...it, [k]: v } : it),
  }));
  const addList = (list, item) => setForm((f) => ({ ...f, [list]: [...f[list], item] }));
  const rmList  = (list, idx)  => setForm((f) => ({ ...f, [list]: f[list].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/sbt-site/contact-settings", {
        ...form,
        map_lat: form.map_lat === "" ? null : Number(form.map_lat),
        map_lng: form.map_lng === "" ? null : Number(form.map_lng),
      });
      toast.success("Contact settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>;

  return (
    <section data-testid="admin-contact">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Contact & Footer Settings</h3>
          <p className="text-xs text-slate-500 mt-0.5">Powers the marketing site&apos;s Contact page, Footer strip, and where inbound form submissions get routed.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={publishNow} disabled={publishing} data-testid="contact-publish-now">
            {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Rocket className="w-3.5 h-3.5 mr-1"/>}
            Publish Now
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="contact-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Save className="w-3.5 h-3.5 mr-1"/>}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="border rounded-lg p-5 bg-white space-y-4">
        <h4 className="text-sm font-semibold text-slate-900">Form Recipients & Support</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Contact form → recipient email</Label><Input value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} placeholder="sales@sbtresearch.com" data-testid="contact-recipient"/></div>
          <div><Label>Support email (footer)</Label><Input value={form.support_email} onChange={(e) => set("support_email", e.target.value)} placeholder="support@sbtresearch.com" data-testid="contact-support"/></div>
          <div><Label>Support phone</Label><Input value={form.support_phone} onChange={(e) => set("support_phone", e.target.value)} placeholder="+91 9975 684 491" data-testid="contact-phone"/></div>
          <div><Label>Support hours</Label><Input value={form.support_hours} onChange={(e) => set("support_hours", e.target.value)} placeholder="Mon–Fri · 9:00 – 18:00 IST" data-testid="contact-hours"/></div>
          <div><Label>Map latitude</Label><Input type="number" value={form.map_lat} onChange={(e) => set("map_lat", e.target.value)} placeholder="18.5204" data-testid="contact-lat"/></div>
          <div><Label>Map longitude</Label><Input type="number" value={form.map_lng} onChange={(e) => set("map_lng", e.target.value)} placeholder="73.8567" data-testid="contact-lng"/></div>
        </div>
      </div>

      <div className="border rounded-lg p-5 bg-white space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">Global Offices (Contact Page)</h4>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="office-reset-defaults"
              onClick={() => {
                if (form.offices.length && !window.confirm(
                  `This will REPLACE the current ${form.offices.length} office row(s) with the 3 default offices (Pune / Singapore / New York). Continue?`
                )) return;
                setForm((f) => ({
                  ...f,
                  offices: [
                    { city: "Pune, India",   role: "Global HQ · Research Desk",
                      address: "Office 401, Tech Park One, Yerwada, Pune 411006",
                      phone: "+91 9975 684 491", email: "info@sbtresearch.com" },
                    { city: "Singapore",     role: "APAC Sales",
                      address: "160 Robinson Road, #14-04 SBF Center, Singapore 068914",
                      phone: "+65 9123 4567", email: "apac@sbtresearch.com" },
                    { city: "New York, USA", role: "AMER Sales",
                      address: "535 5th Avenue, 4th Floor, New York, NY 10017",
                      phone: "+1 (212) 555-0188", email: "amer@sbtresearch.com" },
                  ],
                }));
                toast.info("Default offices loaded — click Save Changes to persist.");
              }}
            >
              Reset to defaults
            </Button>
            <Button variant="outline" size="sm" onClick={() => addList("offices", { city: "", role: "", address: "", phone: "", email: "" })} data-testid="office-add">
              <Plus className="w-3.5 h-3.5 mr-1"/> Add Office
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          Rendered as the &quot;Global offices&quot; side card on <span className="font-mono text-[11px]">/contact/</span>. Order matters — top row shows first.
        </p>
        {form.offices.length === 0 && <div className="text-xs text-slate-500 italic">No offices yet.</div>}
        {form.offices.map((o, i) => (
          <div key={i} className="border rounded p-3" data-testid={`office-row-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px]">City *</Label>
                <Input value={o.city ?? o.label ?? ""} onChange={(e) => setList("offices", i, "city", e.target.value)} placeholder="Pune, India" data-testid={`office-city-${i}`}/>
              </div>
              <div>
                <Label className="text-[11px]">Role / Function</Label>
                <Input value={o.role || ""} onChange={(e) => setList("offices", i, "role", e.target.value)} placeholder="Global HQ · Research Desk" data-testid={`office-role-${i}`}/>
              </div>
              <div>
                <Label className="text-[11px]">Phone</Label>
                <Input value={o.phone} onChange={(e) => setList("offices", i, "phone", e.target.value)} placeholder="+91 9975 684 491"/>
              </div>
              <div>
                <Label className="text-[11px]">Email</Label>
                <Input value={o.email} onChange={(e) => setList("offices", i, "email", e.target.value)} placeholder="info@sbtresearch.com"/>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label className="text-[11px]">Full Address</Label>
                <Input value={o.address} onChange={(e) => setList("offices", i, "address", e.target.value)} placeholder="Office 401, Tech Park One, Yerwada, Pune 411006"/>
              </div>
              <div className="flex items-end justify-end pb-0.5">
                <Button size="sm" variant="ghost" onClick={() => rmList("offices", i)} data-testid={`office-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-600 mr-1"/> Remove
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border rounded-lg p-5 bg-white space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">Social Links</h4>
          <Button variant="outline" size="sm" onClick={() => addList("socials", { platform: "linkedin", url: "" })} data-testid="social-add">
            <Plus className="w-3.5 h-3.5 mr-1"/> Add Social
          </Button>
        </div>
        {form.socials.length === 0 && <div className="text-xs text-slate-500 italic">No socials yet.</div>}
        {form.socials.map((s, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3 items-end" data-testid={`social-row-${i}`}>
            <div><Label className="text-[11px]">Platform</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm bg-white" value={s.platform} onChange={(e) => setList("socials", i, "platform", e.target.value)}>
                {["linkedin","twitter","facebook","instagram","youtube","github","medium","other"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div><Label className="text-[11px]">URL</Label><Input value={s.url} onChange={(e) => setList("socials", i, "url", e.target.value)} placeholder="https://linkedin.com/company/…"/></div>
            <Button size="sm" variant="ghost" onClick={() => rmList("socials", i)}><Trash2 className="w-3.5 h-3.5 text-red-600"/></Button>
          </div>
        ))}
      </div>
    </section>
  );
}
